// Import ScrollTrigger & Lenis
import gsap from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
// Import other animation classes
import {Count} from "./count";
import {Progress} from "./progress";
import {WavyText} from "./wavy-text";
import {Countdown} from './countdown';
import {Gallery} from "./gallery";
import {forEach} from "@splidejs/splide/src/js/utils";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

declare global {
    interface Window {
        scrollTrigger: typeof ScrollTrigger;
        destroyAnimations: () => void;
    }
}

let initialized = false;
const animationInstances: any[] = [];

function initializeAnimations(): void {
    if (initialized) return;
    initialized = true;

    try {
        window.scrollTrigger = ScrollTrigger;

        // Count numbers
        const numberElements = document.querySelectorAll<HTMLElement>("[data-count]");
        numberElements?.forEach((number) => {
            try {
                animationInstances.push(new Count(number));
            } catch (e) {
                console.warn('Failed to initialize Count animation:', e);
            }
        });

        // Progress
        const elements = document.querySelectorAll<HTMLElement>("[data-progress]");
        elements?.forEach((trigger) => {
            try {
                animationInstances.push(new Progress(trigger));
            } catch (e) {
                console.warn('Failed to initialize Progress animation:', e);
            }
        });

        // Waves
        const waves = document.querySelectorAll<HTMLElement>(".wavy-text-component");
        waves?.forEach(wave => {
            try {
                animationInstances.push(new WavyText(wave));
            } catch (e) {
                console.warn('Failed to initialize WavyText animation:', e);
            }
        });

        // Countdown
        const countdowns = document.querySelectorAll<HTMLElement>('.countdown-clock');
        countdowns.forEach(countdown => {
            try {
                animationInstances.push(new Countdown(countdown));
            } catch (e) {
                console.warn('Failed to initialize Countdown animation:', e);
            }
        });

        // Galleries
        const galleries = document.querySelectorAll<HTMLElement>(".gallery-wrapper");
        galleries.forEach(gallery => {
            const lists = gallery.querySelectorAll('.gallery-carousel');

            if (!lists.length) {
                return;
            }

            lists.forEach((list: HTMLElement) => {
                try {
                    animationInstances.push(new Gallery(gallery, list));
                } catch (e) {
                    console.warn('Failed to initialize Gallery animation:', e);
                }
            });
        });

        // Single refresh call after all triggers are created
        if (animationInstances.length > 0) {
            window.scrollTrigger.refresh();

            // Handle hash-based scrolling after a short delay to ensure all animations are initialized
            if (window.location.hash) {
                const target = document.querySelector(window.location.hash);
                if (target) {
                    requestAnimationFrame(() => {
                        target.scrollIntoView();
                    });
                }
            }
        }
    } catch (e) {
        console.error('Failed to initialize animations:', e);
    }
}

function destroyAllAnimations(): void {
    animationInstances.forEach(instance => {
        try {
            instance.destroy?.();
        } catch (e) {
            console.warn('Error destroying animation instance:', e);
        }
    });
    animationInstances.length = 0;

    try {
        window.scrollTrigger?.getAll().forEach((trigger: any) => trigger.kill?.());
    } catch (e) {
        console.warn('Error during cleanup:', e);
    }

    initialized = false;
}

// Expose destroy function globally for manual cleanup if needed
window.destroyAnimations = destroyAllAnimations;

window.addEventListener("load", () => {
    initializeAnimations();
}, {once: true});

if (document.readyState === 'complete') {
    initializeAnimations();
}

window.addEventListener('beforeunload', destroyAllAnimations);